import {
  CircleLoader,
  InputLooks,
  LoaderSizes,
  StandardTextfield,
} from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { FunctionComponent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import useDebounce from '@components/common/custom_hooks/useDebounce';
import { useIdentitySearch, IdentitySearchReturnFormat } from '@components/common/hooks/useIdentitySearch';
import { SuggestedNamesList } from '@components/people_tags/common/SuggestedNamesList';
import { Person } from '@components/people_tags/customPeopleTagTypes';

import { useMergeIdentities } from './hooks/useMergeIdentities';
import { useUpdateFaceName } from './hooks/useUpdateFaceName';
import { MergePeopleModal } from './MergePeopleModal';

const PeopleNameTextfieldWrapper = styled.div`
  position: relative;
  width: 100%;

  .person-details-suggested-names-list {
    width: 100%;
  }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
  border-radius: 4px;
`;

export interface PeopleNameTextfieldProps {
  setPersonLabel: (personLabel: string) => void;
  currentPersonId?: string;
  currentPersonName?: string;
  currentPersonImage?: string;
  /** Face count for the identity; used for People Hub list FieldEdit (NameIdentity) analytics. */
  hubListFaceCountForAnalytics?: number;
  onSelectTargetPerson?: (person: Person) => void;
  onNameChange?: (name: string) => void;
  cancelEdit: () => void;
  onLocalMerge?: (
    sourceId: string,
    targetPerson: { person_id: string; name: string; thumbnail_url: string; facesMoved?: number }
  ) => void;
  onLocalNameUpdate?: (personId: string, newName: string) => void;
  placeholder?: string;
  searchTag?: string;
  showLabel?: boolean;
  textfieldRef?: RefObject<HTMLInputElement>;
  validateInput?: (value: string) => string;
}

export const PeopleNameTextfield: FunctionComponent<PeopleNameTextfieldProps> = ({
  setPersonLabel,
  cancelEdit,
  currentPersonId = undefined,
  currentPersonName = undefined,
  currentPersonImage = undefined,
  hubListFaceCountForAnalytics = 0,
  onLocalMerge = undefined,
  onLocalNameUpdate = undefined,
  placeholder = undefined,
  searchTag = undefined,
  showLabel = false,
  textfieldRef = undefined,
  validateInput = undefined
}) => {
  const [localValue, setLocalValue] = useState(searchTag || '');
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [targetPerson, setTargetPerson] = useState<Person | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [hideDropdown, setHideDropdown] = useState(false);
  const { mergeIdentities } = useMergeIdentities();
  const { updateFaceName } = useUpdateFaceName();

  const hubListAnalytics =
    currentPersonId != null && currentPersonId !== ''
      ? ({ source: 'people_hub_list' as const, faceCount: hubListFaceCountForAnalytics })
      : undefined;

  // Debounce the local value with 600ms delay
  const debouncedValue = useDebounce({
    delay: 600,
    disableDebounce: process.env.NODE_ENV === 'circle',
    value: localValue,
  });

  // Update parent component when debounced value changes
  useEffect(() => {
    if (debouncedValue !== searchTag) {
      setPersonLabel(debouncedValue);
    }
  }, [debouncedValue, setPersonLabel, searchTag]);

  // Handle input change with immediate validation
  const handleInputChange = useCallback((value: string) => {
    const validatedValue = validateInput ? validateInput(value) : value;
    setLocalValue(validatedValue);
  }, [validateInput]);

  // Use API-based search for real-time people search
  const { results: filteredPeople } = useIdentitySearch<Person>({
    searchTerm: debouncedValue,
    isDropdownOpen: !!(debouncedValue && debouncedValue.trim()),
    excludeIdentityId: currentPersonId,
    returnFormat: IdentitySearchReturnFormat.PERSON,
    includeLoading: true
  });

  // Helper function to find exact match by name (case-insensitive)
  const findExactMatch = useCallback((name: string): Person | undefined => {
    return filteredPeople.find((person) =>
      person.tag_name.toLowerCase() === name.toLowerCase()
    );
  }, [filteredPeople]);

  // Show create option when user has typed something AND it's different from current name
  const shouldShowCreateOption = useMemo(() => {
    if (!debouncedValue || !debouncedValue.trim()) {
      return false;
    }

    // Don't show create option if the typed name is the same as current person's name
    if (currentPersonName && debouncedValue.trim().toLowerCase() === currentPersonName.toLowerCase()) {
      return false;
    }

    return true;
  }, [debouncedValue, currentPersonName]);

  // Show dropdown when there are suggestions OR create option should be shown
  const shouldShowDropdown = useMemo(() => {
    if (!debouncedValue || !debouncedValue.trim()) {
      return false;
    }

    // Hide dropdown when merge dialog is open or when selection was made
    if (showMergeDialog || hideDropdown) {
      return false;
    }

    // Show dropdown if there are filtered people OR create option should be shown
    return filteredPeople.length > 0 || shouldShowCreateOption;
  }, [debouncedValue, filteredPeople.length, shouldShowCreateOption, showMergeDialog, hideDropdown]);

  // Removed showAutocompleteResults state and effects - simplified to just check if user has typed something

  const handleOnSelectTargetPerson = (person: Person): void => {
    // Immediately hide dropdown
    setHideDropdown(true);

    // Always show merge dialog when selecting an existing person from dropdown
    // (since selecting = merging current face into that person's identity)
    setTargetPerson(person);
    setShowMergeDialog(true);
  };

  const handleCreateNewPerson = useCallback((name: string): void => {
    // Immediately hide dropdown
    setHideDropdown(true);

    // Check if there's an existing person with the same name using search results
    const existingPerson = findExactMatch(name);

    if (existingPerson) {
      // Show merge dialog
      setTargetPerson(existingPerson);
      setShowMergeDialog(true);
    } else {
      // Update identity name (which creates/names the identity)
      if (currentPersonId && updateFaceName) {
        setIsLoading(true);
        void updateFaceName({
          personId: currentPersonId,
          name,
          old_name: currentPersonName,
          analytics: hubListAnalytics,
        }).then((success) => {
          if (success) {
            onLocalNameUpdate?.(currentPersonId, name);
            setLocalValue(name);
            setPersonLabel(name);
            cancelEdit();
          }
        }).finally(() => {
          setIsLoading(false);
          setHideDropdown(false);
        });
      }
    }
  }, [
    findExactMatch,
    currentPersonId,
    currentPersonName,
    hubListAnalytics,
    updateFaceName,
    onLocalNameUpdate,
    setPersonLabel,
    cancelEdit
  ]);

  const handleMerge = useCallback((): void => {
    if (targetPerson && currentPersonId) {
      if (mergeIdentities) {
        setIsLoading(true);
        void mergeIdentities({
          sourceIdentityId: currentPersonId,
          destinationIdentityId: targetPerson.person_id,
          destinationName: targetPerson.tag_name.trim(),
          sourceName: currentPersonName,
          analyticsPeopleHub: {
            destinationFaceCountBefore: targetPerson.face_count ?? targetPerson.tag_count ?? 0,
          },
        }).then((result) => {
          if (result.success) {
            onLocalMerge?.(currentPersonId, {
              person_id: targetPerson.person_id,
              name: targetPerson.tag_name,
              thumbnail_url: targetPerson.face_uri,
              facesMoved: result.facesMoved
            });
            cancelEdit();
          }
          setShowMergeDialog(false);
          setTargetPerson(undefined);
        }).finally(() => {
          setIsLoading(false);
          setHideDropdown(false);
        });
      } else {
        setShowMergeDialog(false);
        setTargetPerson(undefined);
        cancelEdit();
      }
    } else {
      setShowMergeDialog(false);
      setTargetPerson(undefined);
    }
  }, [
    targetPerson,
    currentPersonId,
    mergeIdentities,
    onLocalMerge,
    cancelEdit,
    currentPersonName
  ]);

  const handleCancelMerge = useCallback((): void => {
    if (targetPerson && currentPersonId && updateFaceName && debouncedValue) {
      setIsLoading(true);
      void updateFaceName({
        personId: currentPersonId,
        name: debouncedValue.trim(),
        old_name: currentPersonName,
        analytics: hubListAnalytics,
      }).then((success) => {
        if (success) {
          onLocalNameUpdate?.(currentPersonId, debouncedValue.trim());
        }
        cancelEdit();
      }).finally(() => {
        setIsLoading(false);
        setHideDropdown(false);
      });
    }
    setShowMergeDialog(false);
    setTargetPerson(undefined);
  }, [
    targetPerson,
    currentPersonId,
    currentPersonName,
    hubListAnalytics,
    updateFaceName,
    debouncedValue,
    onLocalNameUpdate,
    cancelEdit
  ]);

  const handleClose = useCallback((): void => {
    setShowMergeDialog(false);
    setTargetPerson(undefined);
    cancelEdit();
  }, [cancelEdit]);

  // Open merge dialog when user presses Enter
  const handleKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Immediately hide dropdown
      setHideDropdown(true);

      const value = (e.target as HTMLInputElement).value;
      const validatedValue = validateInput ? validateInput(value) : value;
      const trimmedValue = validatedValue.trim();

      // Allow empty string to unname the person
      if (!trimmedValue) {
        if (currentPersonId && updateFaceName) {
          setIsLoading(true);
          void updateFaceName({
            personId: currentPersonId,
            name: '',
            old_name: currentPersonName || '',
            analytics: hubListAnalytics,
          }).then((success) => {
            if (success) {
              onLocalNameUpdate?.(currentPersonId, '');
              setLocalValue('');
              setPersonLabel('');
              cancelEdit();
            }
          }).finally(() => {
            setIsLoading(false);
            setHideDropdown(false);
          });
        }
        return;
      }

      // Check if there's an existing person with the same name using search results
      const existingPerson = findExactMatch(trimmedValue);

      if (existingPerson) {
        // Show merge dialog
        setTargetPerson(existingPerson);
        setShowMergeDialog(true);
      } else {
        handleCreateNewPerson(validatedValue);
      }
    }
  }, [
    findExactMatch,
    currentPersonId,
    handleCreateNewPerson,
    hubListAnalytics,
    updateFaceName,
    onLocalNameUpdate,
    currentPersonName,
    setPersonLabel,
    cancelEdit,
    validateInput
  ]);

  return (
     <>
      <PeopleNameTextfieldWrapper tabIndex={-1}>
        {isLoading && (
          <LoaderOverlay>
            <CircleLoader label="updating" size={LoaderSizes.Small} />
          </LoaderOverlay>
        )}
        <StandardTextfield
          ref={textfieldRef}
          className="tag-person-textfield"
          id="textfield"
          label="Name"
          look={InputLooks.Default}
          onChange={(e): void => handleInputChange(e.target.value)}
          onKeyDown={(e): void => handleKeyDown(e)}
          placeholder={placeholder || t`Add a name`}
          showLabel={showLabel}
          textfieldProps={{
            autoFocus: true,
            autoComplete: 'off',
            className: 'textfield'
          }}
          type="text"
          value={localValue}
        />
        {shouldShowDropdown && (
            <SuggestedNamesList
              handleCreateNewPerson={handleCreateNewPerson}
              handleOnSelectTargetPerson={handleOnSelectTargetPerson}
              listClasses="person-details-suggested-names-list"
              showCreateOption={shouldShowCreateOption}
              suggestions={filteredPeople}
              typedName={localValue}
            />
        )}
      </PeopleNameTextfieldWrapper>

      {showMergeDialog && targetPerson && (
        <MergePeopleModal
          currentPersonThumbnail={currentPersonImage || ''}
          handleCancelMerge={handleCancelMerge}
          handleMerge={handleMerge}
          isOpen={showMergeDialog}
          onClose={handleClose}
          targetPersonThumbnail={targetPerson.face_uri}
        />
      )}
    </>
  );
};
